import { NavigateFunction, useNavigate } from '@rocketlane/rocket-router';
import { useBoolean } from 'ahooks';
import { useEffect, useRef, useState } from 'react';
import { AuthenticationServiceProvider } from 'api/account';
import { triggerOtp } from 'components/pages/two-factor-auth/utils';
import { LS_TEMPLATE_ID_KEY, RoutePaths } from 'constant';
import { useAccountMetaQuery } from 'queries';
import { ActiveTab, ApiKey, getOkta, TokenType } from 'services';
import { getTokenMeta, PermissionType } from 'utils/token';
import { useQueryParams } from './use-query-params';

type Loading = boolean;
type Error = boolean;

export const redirectToSavedTab = (history: NavigateFunction): void => {
  const savedTab = ActiveTab.pop();
  const pathname = savedTab ? new URL(savedTab)?.pathname ?? '/' : '/';
  const hash = savedTab ? new URL(savedTab)?.hash ?? '' : '';

  history(`${pathname}${hash}`, {
    replace: true,
  });
};

export const useLoginWithToken = (
  token: string,
  tokenType: TokenType,
): [Loading, Error] => {
  const history = useNavigate();
  const replace = useNavigate();
  const [isError, setError] = useState(false);
  const [isLoading, { setTrue, setFalse }] = useBoolean(false);
  const params = useQueryParams();
  const externalRefKey = params.get('externalRefKey') ?? '';
  const projectId = params.get('projectId') ?? '';
  const { data } = useAccountMetaQuery();
  const isCognitoEnabled =
    data?.okta?.authenticationServiceProvider ===
    AuthenticationServiceProvider.COGNITO;

  const clientId = data?.okta.clientId;
  // Guards against the effect re-firing after the URL is mutated by post-consume navigation
  // (redirectToSavedTab → token query param drops to ''). Without this, MagicLinkPage re-renders
  // with token='' before unmounting, the effect re-fires, ApiKey.clear() destroys the just-created
  // api-key, and the subsequent ApiKey.create('') 401s — leaving the user logged out.
  const consumed = useRef(false);

  useEffect(() => {
    // Don't consume an empty token (happens during post-redirect re-render), and don't
    // re-consume after a successful run (defense-in-depth against future re-fire causes).
    if (!token || consumed.current) {
      return;
    }

    const loginWithoutOtp = async () => {
      setTrue();
      if (ApiKey.exists()) {
        await ApiKey.clear();
      }
      if (clientId) {
        consumed.current = true;
        try {
          /**
           * Check if magic link contains hash to load the task drawer.
           */

          await ApiKey.create(
            token,
            tokenType,
            clientId,
            externalRefKey,
            projectId,
          );

          getOkta()?.authStateManager.updateAuthState();
          setFalse();
          const templateId = localStorage.getItem(LS_TEMPLATE_ID_KEY);

          localStorage.removeItem(LS_TEMPLATE_ID_KEY);

          if (isCognitoEnabled && templateId) {
            replace(
              RoutePaths.documentTemplate.replace(':templateId', templateId),
            );

            return;
          }

          redirectToSavedTab(history);
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
          consumed.current = false;
          setFalse();
          setError(true);
        }
      }
    };

    const loginWithOtp = async () => {
      try {
        /**
         * Check if magic link contains hash to load the task drawer.
         */

        if (ApiKey.exists()) {
          redirectToSavedTab(history);
        } else {
          consumed.current = true;
          triggerOtp(token);
          history(RoutePaths.magicLink2fa, { state: { token } });
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        consumed.current = false;
        setError(true);
      }
    };

    const exec = () => {
      const { decoded } = getTokenMeta(token);

      const isOTPEnabled =
        decoded?.permission === PermissionType.UserOtp || false;

      if (isOTPEnabled) {
        loginWithOtp();
      } else {
        loginWithoutOtp();
      }
    };

    exec();
  }, [
    token,
    setTrue,
    setFalse,
    history,
    tokenType,
    externalRefKey,
    projectId,
    clientId,
    isCognitoEnabled,
    replace,
  ]);

  return [isLoading, isError];
};
